"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.readHierarchyViewBody = readHierarchyViewBody;
const OrcadDsnCfbStreamUtils_1 = require("./OrcadDsnCfbStreamUtils");
function mergeCursors(states) {
    const merged = new Map();
    for (const state of states) {
        const previous = merged.get(state.offset);
        if (previous === undefined) {
            merged.set(state.offset, state);
        }
        else if (previous.rows !== state.rows || previous.record !== state.record) {
            return undefined;
        }
    }
    return [...merged.values()];
}
function readHierarchyViewBody(bytes, kindOffset, records, diagnostics, streamPath) {
    const fail = (reason) => {
        diagnostics.push({
            severity: 'warning',
            code: 'hierarchy-ownership-body-frame-withheld',
            streamPath,
            offset: kindOffset,
            message: `CFB hierarchy ownership: ${reason}; complete branch body withheld`,
        });
        return undefined;
    };
    const start = kindOffset === 0 ? 0 : kindOffset - 8;
    const initial = records.findUniqueContainingPayload(start);
    if (!initial || kindOffset + 11 > initial.offset + initial.payloadByteLength || bytes[kindOffset] !== 0x42) {
        return fail('named header is not framed');
    }
    const nameLength = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, kindOffset + 9);
    const nameEnd = kindOffset + 11 + nameLength;
    if (nameLength === 0 || nameEnd + 8 > initial.offset + initial.payloadByteLength || bytes[nameEnd] !== 0) {
        return fail('counted branch name crosses its payload');
    }
    const nameBytes = bytes.subarray(kindOffset + 11, nameEnd);
    const name = nameBytes.some((byte) => byte < 0x20 || byte > 0x7e) ? undefined : (OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.bytesToAscii(bytes, kindOffset + 11, nameEnd));
    const metadataLength = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, nameEnd + 1);
    const metadataEnd = nameEnd + 3 + metadataLength;
    if (metadataEnd + 5 > initial.offset + initial.payloadByteLength || bytes[metadataEnd] !== 0) {
        return fail('counted branch metadata crosses its payload');
    }
    let states = [{ record: initial, offset: metadataEnd + 5 }];
    const tables = [
        { kinds: [0x44, 0x45], countBytes: 2, identifiersOnly: false, allowKey: true },
        { kinds: [0x43], countBytes: 2, identifiersOnly: false, allowKey: false },
        { kinds: [0x52], countBytes: 2, identifiersOnly: true, allowKey: false },
        { kinds: [0x5b], countBytes: 4, identifiersOnly: true, allowKey: false },
    ];
    for (const table of tables) {
        const completed = [];
        for (const state of states) {
            if (state.offset + table.countBytes > state.record.offset + state.record.payloadByteLength) {
                continue;
            }
            const count = table.countBytes === 2 ?
                OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, state.offset)
                : OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, state.offset);
            let candidates = [{ ...state, offset: state.offset + table.countBytes }];
            for (let index = 0; index < count && candidates.length > 0; index++) {
                const nextCandidates = [];
                for (const candidate of candidates) {
                    const offset = candidate.offset;
                    const recordEnd = candidate.record.offset + candidate.record.payloadByteLength;
                    const kind = bytes[offset];
                    if (offset + 12 > recordEnd ||
                        !table.kinds.some((allowed) => allowed === kind) ||
                        bytes[offset + 9] !== kind ||
                        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 5) !== 0) {
                        continue;
                    }
                    const propertyCount = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset + 10);
                    const propertyLength = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, offset + 1);
                    if (propertyLength !== 11 + propertyCount * 8 ||
                        offset + 1 + propertyLength !== recordEnd ||
                        candidate.record.byteLength !== candidate.record.payloadByteLength + 8) {
                        continue;
                    }
                    const record = records.get(candidate.record.offset + candidate.record.byteLength);
                    if (!record || record.payloadByteLength < 4) {
                        continue;
                    }
                    const row = {
                        kind,
                        frameOffset: offset,
                        offset: record.offset,
                        occurrenceId: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, record.offset),
                    };
                    const end = record.offset + record.payloadByteLength;
                    if (table.identifiersOnly) {
                        if (record.offset + 8 <= end) {
                            nextCandidates.push({
                                record,
                                offset: record.offset + 8,
                                rows: {
                                    row: { ...row, referenceId: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, record.offset + 4) },
                                    previous: candidate.rows,
                                },
                            });
                        }
                        continue;
                    }
                    if (record.offset + 6 > end) {
                        continue;
                    }
                    const field = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, record.offset + 4);
                    if (table.allowKey) {
                        nextCandidates.push({
                            record,
                            offset: record.offset + 6,
                            rows: { row: { ...row, key: field }, previous: candidate.rows },
                        });
                    }
                    const afterName = record.offset + 6 + field;
                    if (afterName < end &&
                        bytes[afterName] === 0 &&
                        !bytes.subarray(record.offset + 6, afterName).some((byte) => byte < 0x20 || byte > 0x7e)) {
                        nextCandidates.push({
                            record,
                            offset: afterName + 1,
                            rows: {
                                row: { ...row, name: OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.bytesToAscii(bytes, record.offset + 6, afterName) },
                                previous: candidate.rows,
                            },
                        });
                    }
                }
                const mergedCandidates = mergeCursors(nextCandidates);
                if (mergedCandidates === undefined) {
                    return fail('counted table framing is ambiguous');
                }
                candidates = mergedCandidates;
            }
            completed.push(...candidates);
        }
        const mergedStates = mergeCursors(completed);
        if (mergedStates === undefined) {
            return fail('counted table framing is ambiguous');
        }
        states = mergedStates;
    }
    states = states.filter((state) => {
        const end = bodyEnd(bytes, state);
        return end !== undefined && hasChildListHeader(bytes, end);
    });
    if (states.length !== 1) {
        return fail(states.length === 0 ? 'counted table framing is incomplete' : 'counted table framing is ambiguous');
    }
    const state = states[0];
    const endOffset = bodyEnd(bytes, state);
    if (endOffset === undefined) {
        return fail('body continuation is incomplete');
    }
    const rows = [];
    for (let link = state.rows; link !== undefined; link = link.previous) {
        rows.push(link.row);
    }
    return {
        name,
        endOffset,
        rows: rows.reverse(),
    };
}
function bodyEnd(bytes, state) {
    if (OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.hasBytes(bytes, state.offset, [0xff, 0xe4, 0x5c, 0x39])) {
        if (state.offset + 8 > bytes.length) {
            return undefined;
        }
        const length = OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, state.offset + 4);
        const end = state.offset + 8 + length;
        const payloadEnd = state.record.offset + state.record.payloadByteLength;
        const closesRecord = length === 0 && state.offset === payloadEnd && end === state.record.offset + state.record.byteLength;
        return end <= payloadEnd || closesRecord ? end : undefined;
    }
    return state.offset < state.record.offset + state.record.payloadByteLength ? state.offset : undefined;
}
function hasChildListHeader(bytes, offset) {
    if (offset + 2 > bytes.length) {
        return false;
    }
    if (OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, offset) === 0) {
        return true;
    }
    const base = offset + 2;
    if (base + 20 > bytes.length ||
        bytes[base] !== 0x42 ||
        bytes[base + 9] !== 0x42 ||
        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, base + 5) !== 0) {
        return false;
    }
    const end = base + 9 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, base + 1);
    const terminator = base + 12 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt16LE(bytes, base + 10) * 8;
    return (end + 19 <= bytes.length &&
        terminator + 8 <= end &&
        OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.hasBytes(bytes, terminator, [0xff, 0xe4, 0x5c, 0x39]) &&
        terminator + 8 + OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.readUInt32LE(bytes, terminator + 4) === end &&
        bytes[end + 8] === 0x42);
}
//# sourceMappingURL=OrcadDsnCfbHierarchyViewFrames.js.map